import { filterTruthy, parseU64OrZero } from "@bridgesplit/utils";

import {
    ParsedCollateralTermsApi,
    ParsedUpdateAssetDataParamsApi,
    ParsedAddCollateralTimelockArgsApi,
    ParsedUpdateStrategySettingsParamsApi,
    ParsedUpdateVaultSettingsParamsApi,
    ParsedTimeLockUpdateParamsApi,
    UpdateCapsParamsApi,
    PaginatedTimeLockResponseApi,
    ParsedTimeLockApi
} from "../api";
import {
    PaginatedTimeLockResponse,
    ParsedAddCollateralTimelockArgs,
    ParsedCollateralTerms,
    ParsedPrincipalCaps,
    ParsedTimeLock,
    ParsedTimeLockUpdateParams,
    ParsedUpdateAssetDataParams,
    ParsedUpdateStrategySettingsParams,
    ParsedUpdateVaultSettingsParams,
    TimeLockUpdateType,
    UpdateCapsParams
} from "../domain";
import { TimeLockStatus } from "../timelock";

function parseCollateralTermsFromApi(api: ParsedCollateralTermsApi): ParsedCollateralTerms {
    return {
        mint: api.mint,
        terms: api.terms.map((term) => parseU64OrZero(term))
    };
}

function parseUpdateAssetDataParamsFromApi(api: ParsedUpdateAssetDataParamsApi): ParsedUpdateAssetDataParams {
    return {
        assetIdentifier: api.assetIdentifier,
        quoteMint: api.quoteMint,
        oracleAccount: api.oracleAccount,
        oracleType: api.oracleType,
        maxUncertainty: api.maxUncertainty,
        maxAge: api.maxAge,
        ltv: api.ltv,
        liquidationThreshold: api.liquidationThreshold,
        maxCollateralAllocationPct: api.maxCollateralAllocationPct
            ? parseU64OrZero(api.maxCollateralAllocationPct)
            : undefined,
        removeCollateral: api.removeCollateral
    };
}

function parseAddCollateralTimelockArgsFromApi(
    api: ParsedAddCollateralTimelockArgsApi
): ParsedAddCollateralTimelockArgs {
    return {
        collateralTerms: parseCollateralTermsFromApi(api.collateralTerms),
        updateAssetDataParams: parseUpdateAssetDataParamsFromApi(api.updateAssetDataParams)
    };
}

function parseUpdateStrategySettingsParamsFromApi(
    api: ParsedUpdateStrategySettingsParamsApi
): ParsedUpdateStrategySettingsParams {
    return {
        liquidityBuffer: api.liquidityBuffer,
        interestFee: api.interestFee,
        originationFee: api.originationFee,
        principalFee: api.principalFee,
        originationCap: api.originationCap,
        externalYieldSourceArgs: api.externalYieldSourceArgs,
        originationsEnabled: api.originationsEnabled,
        marketInformation: api.marketInformation
    };
}

function parseUpdateVaultSettingsParamsFromApi(
    api: ParsedUpdateVaultSettingsParamsApi
): ParsedUpdateVaultSettingsParams {
    return {
        maxEarlyUnstakeFeeCbps: Number(api.maxEarlyUnstakeFeeCbps)
    };
}

function parsePrincipalCapsFromApi(api: {
    max1hr: string;
    max24hr: string;
    maxOutstanding: string;
}): ParsedPrincipalCaps {
    return {
        max1hr: parseU64OrZero(api.max1hr),
        max24hr: parseU64OrZero(api.max24hr),
        maxOutstanding: parseU64OrZero(api.maxOutstanding)
    };
}

function parseUpdateCapsParamsFromApi(api: UpdateCapsParamsApi): UpdateCapsParams {
    return {
        borrowCaps: api.borrowCaps ? parsePrincipalCapsFromApi(api.borrowCaps) : undefined,
        supplyCaps: api.supplyCaps ? parsePrincipalCapsFromApi(api.supplyCaps) : undefined,
        withdrawCaps: api.withdrawCaps ? parsePrincipalCapsFromApi(api.withdrawCaps) : undefined
    };
}

function parseTimeLockUpdateParamsFromApi(
    api: ParsedTimeLockUpdateParamsApi | null | undefined
): ParsedTimeLockUpdateParams | null {
    switch (api?.type) {
        case "updateStrategyMarketInformation":
            return {
                type: TimeLockUpdateType.UpdateStrategyMarketInformation,
                params: api.params
            };
        case "addCollateral":
            return {
                type: TimeLockUpdateType.AddCollateral,
                params: parseAddCollateralTimelockArgsFromApi(api.params)
            };
        case "updateLtv":
            return {
                type: TimeLockUpdateType.UpdateLtv,
                params: parseUpdateAssetDataParamsFromApi(api.params)
            };
        case "removeCollateral":
            return {
                type: TimeLockUpdateType.RemoveCollateral,
                params: parseCollateralTermsFromApi(api.params)
            };
        case "updateApy":
            return {
                type: TimeLockUpdateType.UpdateApy,
                params: parseCollateralTermsFromApi(api.params)
            };
        case "updateStrategySettings":
            return {
                type: TimeLockUpdateType.UpdateStrategySettings,
                params: parseUpdateStrategySettingsParamsFromApi(api.params)
            };
        case "updateVaultSettings":
            return {
                type: TimeLockUpdateType.UpdateVaultSettings,
                params: parseUpdateVaultSettingsParamsFromApi(api.params)
            };
        case "updateCaps":
            return {
                type: TimeLockUpdateType.CapsUpdate,
                params: parseUpdateCapsParamsFromApi(api.params)
            };
        case "updateCollateralAllocationPct":
            return {
                type: TimeLockUpdateType.UpdateCollateralAllocationPct,
                params: parseUpdateAssetDataParamsFromApi(api.params)
            };
        default: {
            return null;
        }
    }
}

function parseTimeLockFromApi(api: ParsedTimeLockApi): ParsedTimeLock | null {
    const update = parseTimeLockUpdateParamsFromApi(api.update);
    if (update === null) return null;

    return {
        address: api.address,
        vaultAddress: api.vaultAddress,
        principalMint: api.principalMint,
        initializationTimestamp: api.initializationTimestamp,
        executionTimestamp: api.executionTimestamp,
        status: api.status as TimeLockStatus,
        update
    };
}

export function parsePaginatedTimeLockResponseFromApi(api: PaginatedTimeLockResponseApi): PaginatedTimeLockResponse {
    return {
        timeLocks: api.timeLocks.map(parseTimeLockFromApi).filter(filterTruthy),
        total: api.total
    };
}
